/**
 * Legacy GraphQL Client
 *
 * ⚠️  DEPRECATION NOTICE ⚠️
 *
 * This file contains the legacy GraphQL client and query strings.
 * NEW CODE SHOULD USE URQL HOOKS instead:
 *
 *   // ❌ Old way (deprecated)
 *   import { graphql, EVENTS_QUERY } from "@/lib/graphql";
 *   const data = await graphql(EVENTS_QUERY, { ... });
 *
 *   // ✅ New way (preferred)
 *   import { useQuery, useMutation } from "urql";
 *   const [result] = useQuery({ query: MyDocument });
 *
 * Benefits of URQL:
 * - Automatic caching with normalized cache
 * - Circuit breaker protection against cascading failures
 * - Idempotency for mutations (prevents double purchases)
 * - Better error handling and TypeScript support
 * - Automatic cache invalidation after mutations
 *
 * Migration plan:
 * 1. New features should use URQL hooks exclusively
 * 2. Existing pages will be migrated incrementally
 * 3. This file will be removed after full migration
 *
 * See: apps/webapp/src/api/client.ts for URQL configuration
 */

import { config } from "../config.ts";
import { getInitData } from "./telegram.ts";
import { getStoredTokens } from "./storage.ts";
import { errorTracking } from "./error-tracking.ts";

const GRAPHQL_URL = `${config.apiUrl}/graphql`;

// Request timeout in milliseconds
const REQUEST_TIMEOUT = 30000;

// Minimum loader display time for better UX (prevents flickering)
const MIN_LOADER_TIME = 300;

/**
 * Ensures a minimum execution time for async operations.
 * This prevents loader "flickering" when data loads too fast.
 * The loader will be shown for at least MIN_LOADER_TIME ms.
 */
export async function withMinDelay<T>(promise: Promise<T>): Promise<T> {
  const start = Date.now();
  const result = await promise;
  const elapsed = Date.now() - start;

  if (elapsed < MIN_LOADER_TIME) {
    await new Promise((resolve) =>
      setTimeout(resolve, MIN_LOADER_TIME - elapsed)
    );
  }

  return result;
}

interface GraphQLResponse<T> {
  data?: T;
  errors?: Array<{ message: string; extensions?: { code?: string } }>;
}

/**
 * Custom error class for GraphQL errors
 */
export class GraphQLClientError extends Error {
  public readonly code: string;
  public readonly isNetworkError: boolean;
  public readonly isTimeout: boolean;

  constructor(
    message: string,
    options: {
      code?: string;
      isNetworkError?: boolean;
      isTimeout?: boolean;
    } = {}
  ) {
    super(message);
    this.name = "GraphQLClientError";
    this.code = options.code ?? "UNKNOWN";
    this.isNetworkError = options.isNetworkError ?? false;
    this.isTimeout = options.isTimeout ?? false;
  }
}

/**
 * Build auth headers for GraphQL and API requests
 *
 * @deprecated Use `import { getAuthHeaders } from "@/lib/api-headers"` instead.
 * This function is kept for backward compatibility.
 *
 * Priority:
 * 1. Telegram initData (x-telegram-init-data header) - for Mini App
 * 2. JWT Bearer token (for PWA mode)
 */
export function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    // CSRF defense-in-depth
    "X-Requested-With": "XMLHttpRequest",
  };

  // 1. Try Telegram initData (Mini App mode)
  const initData = getInitData();
  if (initData) {
    headers["x-telegram-init-data"] = initData;
    return headers;
  }

  // 2. Try JWT Bearer token (PWA mode)
  const tokens = getStoredTokens();
  if (tokens?.accessToken) {
    headers["Authorization"] = `Bearer ${tokens.accessToken}`;
  }

  return headers;
}

/**
 * Extract operation name from GraphQL query string for breadcrumbs
 */
function extractOperationName(query: string): string {
  // Match "query OperationName" or "mutation OperationName"
  const match = query.match(/(?:query|mutation)\s+(\w+)/i);
  return match?.[1] ?? "unknown";
}

/**
 * Execute GraphQL query/mutation with proper error handling.
 * 
 * NOTE: This is a legacy function used in admin pages and auth-context.
 * New code should use URQL hooks instead for better caching and circuit breaker protection.
 * 
 * @deprecated Prefer URQL hooks (useQuery, useMutation) for new code.
 */
export async function graphql<T>(
  query: string,
  variables?: Record<string, unknown>
): Promise<T> {
  const operationName = extractOperationName(query);
  
  // Add breadcrumb for tracking legacy GraphQL calls
  errorTracking.addBreadcrumb({
    category: "graphql.legacy",
    message: `GraphQL request: ${operationName}`,
    level: "info",
    data: {
      operation: operationName,
      hasVariables: !!variables,
    },
  });

  // Create abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const response = await fetch(GRAPHQL_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ query, variables }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // Handle HTTP errors
    if (!response.ok) {
      const statusText = response.statusText || `HTTP ${response.status}`;
      const error = new GraphQLClientError(`Server error: ${statusText}`, {
        code: `HTTP_${response.status}`,
        isNetworkError: response.status >= 500,
      });
      
      errorTracking.addBreadcrumb({
        category: "graphql.legacy",
        message: `GraphQL HTTP error: ${operationName}`,
        level: "error",
        data: {
          operation: operationName,
          status: response.status,
          code: error.code,
        },
      });
      
      throw error;
    }

    // Parse response
    let json: GraphQLResponse<T>;
    try {
      json = await response.json();
    } catch {
      const error = new GraphQLClientError("Invalid server response", {
        code: "PARSE_ERROR",
      });
      
      errorTracking.addBreadcrumb({
        category: "graphql.legacy",
        message: `GraphQL parse error: ${operationName}`,
        level: "error",
        data: { operation: operationName },
      });
      
      throw error;
    }

    // Handle GraphQL errors
    if (json.errors?.length) {
      const gqlError = json.errors[0];
      const error = new GraphQLClientError(gqlError?.message ?? "GraphQL error", {
        code: gqlError?.extensions?.code ?? "GRAPHQL_ERROR",
      });
      
      errorTracking.addBreadcrumb({
        category: "graphql.legacy",
        message: `GraphQL error: ${operationName}`,
        level: "error",
        data: {
          operation: operationName,
          code: error.code,
          message: gqlError?.message,
        },
      });
      
      throw error;
    }

    // Handle missing data
    if (!json.data) {
      const error = new GraphQLClientError("No data returned", {
        code: "NO_DATA",
      });
      
      errorTracking.addBreadcrumb({
        category: "graphql.legacy",
        message: `GraphQL no data: ${operationName}`,
        level: "warning",
        data: { operation: operationName },
      });
      
      throw error;
    }

    // Success breadcrumb
    errorTracking.addBreadcrumb({
      category: "graphql.legacy",
      message: `GraphQL success: ${operationName}`,
      level: "info",
      data: { operation: operationName },
    });

    return json.data;
  } catch (error) {
    clearTimeout(timeoutId);

    // Re-throw our custom errors (already have breadcrumbs)
    if (error instanceof GraphQLClientError) {
      throw error;
    }

    // Handle abort/timeout
    if (error instanceof DOMException && error.name === "AbortError") {
      const timeoutError = new GraphQLClientError("Request timeout", {
        code: "TIMEOUT",
        isTimeout: true,
      });
      
      errorTracking.addBreadcrumb({
        category: "graphql.legacy",
        message: `GraphQL timeout: ${operationName}`,
        level: "error",
        data: { operation: operationName },
      });
      
      throw timeoutError;
    }

    // Handle network errors
    if (error instanceof TypeError) {
      const networkError = new GraphQLClientError("Network error. Check your connection.", {
        code: "NETWORK_ERROR",
        isNetworkError: true,
      });
      
      errorTracking.addBreadcrumb({
        category: "graphql.legacy",
        message: `GraphQL network error: ${operationName}`,
        level: "error",
        data: { operation: operationName },
      });
      
      throw networkError;
    }

    // Unknown error
    const message =
      error instanceof Error ? error.message : "Unknown error occurred";
    
    errorTracking.addBreadcrumb({
      category: "graphql.legacy",
      message: `GraphQL unknown error: ${operationName}`,
      level: "error",
      data: { operation: operationName, message },
    });
    
    throw new GraphQLClientError(message, { code: "UNKNOWN" });
  }
}

/**
 * Patterns that indicate technical/internal error messages
 * that should not be shown to users
 */
const TECHNICAL_ERROR_PATTERNS = [
  /cannot read prop/i,
  /undefined is not/i,
  /null is not/i,
  /is not a function/i,
  /unexpected token/i,
  /syntax error/i,
  /internal server error/i,
  /database/i,
  /sql/i,
  /prisma/i,
  /connection refused/i,
  /econnrefused/i,
  /socket hang up/i,
  /etimedout/i,
  /enotfound/i,
  /fetch failed/i,
  /network request failed/i,
  /failed to fetch/i,
  /abort/i,
  /cannot represent/i,
  /graphql/i,
  /query/i,
  /mutation/i,
  /subscription/i,
  /resolver/i,
  /at\s+\w+\s+\(/i, // Stack trace pattern
  /^\s*at\s+/m, // Stack trace line
  /\.ts:\d+/i, // TypeScript file reference
  /\.js:\d+/i, // JavaScript file reference
  /node_modules/i,
  /webpack/i,
  /module/i,
  /export/i,
  /import/i,
];

/**
 * Sanitize an error message to ensure it's user-friendly.
 * Filters out technical details and returns a fallback for technical errors.
 * 
 * @param message - The error message to sanitize
 * @param fallback - Fallback message if the original is technical
 * @returns User-friendly error message
 */
export function sanitizeErrorMessage(
  message: string | undefined | null,
  fallback: string = "Произошла ошибка"
): string {
  if (!message) return fallback;
  
  // If message is too long, it's likely a stack trace or detailed error
  if (message.length > 200) {
    return fallback;
  }
  
  // Check against technical patterns
  for (const pattern of TECHNICAL_ERROR_PATTERNS) {
    if (pattern.test(message)) {
      return fallback;
    }
  }
  
  // If contains file paths (likely a stack trace)
  if (/[/\\][\w-]+\.(ts|js|tsx|jsx)/i.test(message)) {
    return fallback;
  }
  
  // If looks like a code/variable name (camelCase or SCREAMING_SNAKE_CASE)
  if (/^[a-z]+[A-Z][a-zA-Z]*$/.test(message) || /^[A-Z_]+$/.test(message)) {
    return fallback;
  }
  
  return message;
}

/**
 * Helper to get user-friendly error message.
 * Sanitizes all error messages to prevent showing technical details to users.
 * 
 * @param error - The error to get message from
 * @param fallback - Optional fallback message (defaults to generic Russian message)
 * @returns User-friendly error message
 */
export function getErrorMessage(
  error: unknown,
  fallback: string = "Произошла ошибка"
): string {
  if (error instanceof GraphQLClientError) {
    if (error.isTimeout) {
      return "Превышено время ожидания. Попробуйте снова.";
    }
    if (error.isNetworkError) {
      return "Ошибка сети. Проверьте подключение к интернету.";
    }
    // Map technical error codes to user-friendly messages
    switch (error.code) {
      case "UNAUTHENTICATED":
        return "Требуется авторизация. Перезапустите приложение.";
      case "FORBIDDEN":
        return "Недостаточно прав для выполнения действия.";
      case "NOT_FOUND":
        return "Запрашиваемые данные не найдены.";
      case "BAD_REQUEST":
      case "VALIDATION_ERROR":
        return "Некорректные данные. Проверьте введённую информацию.";
      case "INTERNAL_SERVER_ERROR":
        return "Ошибка сервера. Попробуйте позже.";
      default:
        // Sanitize the error message
        return sanitizeErrorMessage(error.message, fallback);
    }
  }

  if (error instanceof Error) {
    // Sanitize all Error messages
    return sanitizeErrorMessage(error.message, fallback);
  }

  if (typeof error === "string") {
    return sanitizeErrorMessage(error, fallback);
  }

  return fallback;
}

/** Check if error is network-related (for showing heart-style error UI) */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof GraphQLClientError) {
    return error.isNetworkError || error.isTimeout;
  }
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes("network") ||
      msg.includes("fetch") ||
      msg.includes("failed to fetch") ||
      msg.includes("networkerror") ||
      msg.includes("connection") ||
      msg.includes("econnrefused") ||
      msg.includes("timeout")
    );
  }
  return false;
}

// ============================================================================
// Queries
// ============================================================================

/** Events for schedule. Optional: categoryIds; timezone + fromDate/toDate; onlyWithMyRsvp; homeGroupId. */
export const EVENTS_QUERY = `
  query Events($categoryIds: [ID!], $timezone: String, $fromDate: String, $toDate: String, $onlyWithMyRsvp: Boolean, $homeGroupId: ID) {
    events(categoryIds: $categoryIds, timezone: $timezone, fromDate: $fromDate, toDate: $toDate, onlyWithMyRsvp: $onlyWithMyRsvp, homeGroupId: $homeGroupId) {
      id
      status
      isActive
      title
      subtitle
      description
      venue
      address
      mapsUrl
      startsAt
      imageUrl
      videoUrl
      streamYoutubeUrl
      streamVkUrl
      telegramChatUrl
      inviteText
      slogan
      program
      requiresRsvpConfirmation
      myRsvp {
        id
        status
      }
      hasMyTickets
      categories {
        id
        name
        code
        color
      }
      ticketTypes {
        id
        name
        description
        price
        totalPrice
        currency
        maxQuantity
        soldCount
        available
      }
    }
  }
`;

/** Active categories for schedule filter (top-level). */
export const SCHEDULE_CATEGORIES_QUERY = `
  query ScheduleCategories {
    categories(includeChildren: true) {
      id
      name
      code
      color
      sortOrder
    }
  }
`;

/** Category by code (e.g. "worship" for worship page). */
export const CATEGORY_BY_CODE_QUERY = `
  query CategoryByCode($code: String!) {
    categoryByCode(code: $code) {
      id
      name
      code
      color
    }
  }
`;

/** События с featured=true — показываются в блоке «Анонсы» на главной */
export const ANNOUNCEMENT_EVENTS_QUERY = `
  query AnnouncementEvents {
    announcementEvents {
      id
      title
      description
      venue
      startsAt
      imageUrl
      bannerImageUrl
      ticketTypes {
        id
        name
        description
        price
        totalPrice
        currency
        maxQuantity
        soldCount
        available
      }
    }
  }
`;

// =============================================================================
// Feedback, Pastor, Church
// =============================================================================

export const CHURCH_INFO_QUERY = `
  query ChurchInfo {
    churchInfo {
      id
      title
      imageUrl
      welcomeText
      scheduleText
      address
      mapUrl
      parkingText
      phone
      email
      companyName
      companyDetails
      usefulChats {
        label
        url
      }
      isActive
      welcomeHeroVideoUrl
      welcomeHeroImageUrl
      welcomeHeroTitle
      welcomeHeroSubtitle
      welcomeAboutTitle
      welcomeAboutText
      welcomeDaysCount
      welcomeMissionText
    }
  }
`;

export const HOME_GROUPS_PAGE_CONFIG_QUERY = `
  query HomeGroupsPageConfig {
    homeGroupsPageConfig {
      id
      title
      bodyText
      imageUrl
      isActive
    }
  }
`;

export const ADMIN_HOME_GROUPS_FOR_SELECT_QUERY = `
  query AdminHomeGroupsForSelect {
    adminHomeGroups {
      id
      name
    }
  }
`;

export const ADMIN_HOME_GROUPS_PAGE_CONFIG_QUERY = `
  query AdminHomeGroupsPageConfig {
    adminHomeGroupsPageConfig {
      id
      title
      bodyText
      imageUrl
      isActive
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_HOME_GROUPS_PAGE_CONFIG_MUTATION = `
  mutation UpdateHomeGroupsPageConfig($input: HomeGroupsPageConfigInput!) {
    updateHomeGroupsPageConfig(input: $input) {
      id
      title
      bodyText
      imageUrl
      isActive
    }
  }
`;

export const HOME_GROUPS_QUERY = `
  query HomeGroups($filter: HomeGroupFilter) {
    homeGroups(filter: $filter) {
      id
      name
      description
      location
      isOpen
      isActive
      memberCount
      imageUrl
      mapsUrl
      leader {
        id
        firstName
        lastName
      }
      schedules {
        id
        dayOfWeek
        startTime
        endTime
        isActive
      }
    }
  }
`;

export const HOME_GROUP_QUERY = `
  query HomeGroup($id: ID!) {
    homeGroup(id: $id) {
      id
      name
      description
      isOpen
      isMember
      memberCount
      imageUrl
      leader {
        id
        firstName
        lastName
        username
      }
      coLeader {
        id
        firstName
        lastName
        username
      }
      schedules {
        id
        dayOfWeek
        startTime
        endTime
        isActive
      }
    }
  }
`;

export const JOIN_HOME_GROUP_MUTATION = `
  mutation JoinHomeGroup($homeGroupId: ID!) {
    joinHomeGroup(homeGroupId: $homeGroupId) {
      id
    }
  }
`;

export const CREATE_FEEDBACK_MUTATION = `
  mutation CreateFeedback($text: String!, $authorName: String) {
    createFeedback(text: $text, authorName: $authorName) {
      id
      createdAt
    }
  }
`;

export const CREATE_PASTOR_MESSAGE_MUTATION = `
  mutation CreatePastorMessage($text: String!, $authorName: String) {
    createPastorMessage(text: $text, authorName: $authorName) {
      id
      createdAt
    }
  }
`;

// Admin: feedback
export const ADMIN_FEEDBACK_MESSAGES_QUERY = `
  query AdminFeedbackMessages($filter: AdminFeedbackMessagesFilter) {
    adminFeedbackMessages(filter: $filter) {
      id
      accountId
      telegramId
      authorName
      text
      status
      adminNotes
      createdAt
      updatedAt
    }
  }
`;

export const ADMIN_UPDATE_FEEDBACK_MUTATION = `
  mutation AdminUpdateFeedback($id: ID!, $status: FeedbackStatus, $adminNotes: String) {
    adminUpdateFeedback(id: $id, status: $status, adminNotes: $adminNotes) {
      id
      status
      adminNotes
    }
  }
`;

export const ADMIN_FEEDBACK_CONFIG_QUERY = `
  query AdminFeedbackConfig {
    adminFeedbackConfig {
      accountId
      account {
        id
        firstName
        lastName
        username
        telegramId
      }
    }
  }
`;

export const ADMIN_SET_FEEDBACK_CONFIG_MUTATION = `
  mutation AdminSetFeedbackConfig($accountId: ID!) {
    adminSetFeedbackConfig(accountId: $accountId) {
      accountId
      account {
        id
        firstName
        lastName
        username
        telegramId
      }
    }
  }
`;

export const ADMIN_CLEAR_FEEDBACK_CONFIG_MUTATION = `
  mutation AdminClearFeedbackConfig {
    adminClearFeedbackConfig
  }
`;

// Admin: pastor
export const ADMIN_PASTOR_CONFIG_QUERY = `
  query AdminPastorConfig {
    adminPastorConfig {
      accountId
      account {
        id
        firstName
        lastName
        username
        telegramId
      }
    }
  }
`;

export const ADMIN_PASTOR_MESSAGES_QUERY = `
  query AdminPastorMessages {
    adminPastorMessages {
      id
      accountId
      telegramId
      authorName
      text
      createdAt
    }
  }
`;

export const ADMIN_SET_PASTOR_CONFIG_MUTATION = `
  mutation AdminSetPastorConfig($accountId: ID!) {
    adminSetPastorConfig(accountId: $accountId) {
      accountId
      account {
        id
        firstName
        lastName
        username
        telegramId
      }
    }
  }
`;

export const ADMIN_CLEAR_PASTOR_CONFIG_MUTATION = `
  mutation AdminClearPastorConfig {
    adminClearPastorConfig
  }
`;

// Admin: church info
export const ADMIN_CHURCH_INFO_QUERY = `
  query AdminChurchInfo {
    adminChurchInfo {
      id
      title
      imageUrl
      welcomeText
      scheduleText
      address
      mapUrl
      parkingText
      phone
      email
      companyName
      companyDetails
      usefulChats {
        label
        url
      }
      isActive
      createdAt
      updatedAt
      welcomeHeroVideoUrl
      welcomeHeroImageUrl
      welcomeHeroTitle
      welcomeHeroSubtitle
      welcomeAboutTitle
      welcomeAboutText
      welcomeDaysCount
      welcomeMissionText
    }
  }
`;

export const UPDATE_CHURCH_INFO_MUTATION = `
  mutation UpdateChurchInfo($input: ChurchInfoInput!) {
    updateChurchInfo(input: $input) {
      id
      title
      imageUrl
      welcomeText
      scheduleText
      address
      mapUrl
      parkingText
      phone
      email
      companyName
      companyDetails
      usefulChats {
        label
        url
      }
      isActive
      welcomeHeroVideoUrl
      welcomeHeroImageUrl
      welcomeHeroTitle
      welcomeHeroSubtitle
      welcomeAboutTitle
      welcomeAboutText
      welcomeDaysCount
      welcomeMissionText
    }
  }
`;

export const EVENT_QUERY = `
  query Event($id: ID!, $preview: Boolean) {
    event(id: $id, preview: $preview) {
      id
      title
      subtitle
      description
      venue
      address
      mapsUrl
      startsAt
      endsAt
      imageUrl
      videoUrl
      categories {
        id
        name
      }
      streamYoutubeUrl
      streamVkUrl
      telegramChatUrl
      inviteText
      slogan
      program
      isActive
      requiresRsvpConfirmation
      hasMyTickets
      myRsvp {
        id
        status
      }
      ticketTypes {
        id
        name
        description
        price
        totalPrice
        currency
        maxQuantity
        soldCount
        available
        feesBreakdown {
          name
          type
          amount
        }
      }
      homeGroup {
        id
        name
      }
    }
  }
`;

export const SET_EVENT_RSVP_MUTATION = `
  mutation SetEventRsvp($eventId: ID!, $status: RsvpStatus!) {
    setEventRsvp(eventId: $eventId, status: $status) {
      id
      status
    }
  }
`;

export const REMOVE_EVENT_RSVP_MUTATION = `
  mutation RemoveEventRsvp($eventId: ID!) {
    removeEventRsvp(eventId: $eventId)
  }
`;

export const ADMIN_EVENT_PENDING_RSVPS_QUERY = `
  query AdminEventPendingRsvps($eventId: ID!) {
    adminEventPendingRsvps(eventId: $eventId) {
      id
      accountId
      status
      account { id firstName lastName }
    }
  }
`;

export const CONFIRM_EVENT_RSVP_MUTATION = `
  mutation ConfirmEventRsvp($eventId: ID!, $accountId: ID!) {
    confirmEventRsvp(eventId: $eventId, accountId: $accountId) {
      id
      status
    }
  }
`;

export const ORDER_QUERY = `
  query Order($id: ID!) {
    order(id: $id) {
      id
      status
      subtotal
      feesTotal
      totalAmount
      currency
      expiresAt
      event {
        id
        title
        startsAt
        venue
        imageUrl
      }
      items {
        id
        quantity
        unitPrice
        totalPrice
        ticketType {
          id
          name
        }
      }
      fees {
        id
        name
        type
        amount
      }
      pricingOptions {
        id
        currency
        subtotal
        feesTotal
        total
        isSelected
        fees {
          id
          name
          type
          calculation
          amount
        }
      }
      selectedPricing {
        id
        currency
        subtotal
        feesTotal
        total
        fees {
          id
          name
          type
          calculation
          amount
        }
      }
      manualPayment {
        status
        receiptUrl
      }
      tickets {
        id
        ticketNumber
        ticketType { name }
        event {
          id
          title
          startsAt
          venue
        }
      }
      conversationMessages {
        id
        messageText
        createdAt
        isFromUser
      }
    }
  }
`;

export const ORDERS_QUERY = `
  query Orders {
    orders {
      id
      status
      totalAmount
      currency
      createdAt
      event {
        id
        title
        startsAt
        imageUrl
      }
      items {
        quantity
        ticketType {
          name
        }
      }
    }
  }
`;

export const TICKETS_QUERY = `
  query Tickets {
    tickets {
      id
      ticketNumber
      imageUrl
      issuedAt
      ticketType {
        name
      }
      event {
        id
        title
        startsAt
        venue
      }
    }
  }
`;

export const TICKET_QUERY = `
  query Ticket($id: ID!) {
    ticket(id: $id) {
      id
      ticketNumber
      issuedAt
      ticketType {
        name
      }
      event {
        id
        title
        startsAt
        venue
      }
    }
  }
`;

export const PAYMENT_REQUISITES_QUERY = `
  query PaymentRequisites {
    paymentRequisites {
      id
      currency
      bank
      account
      name
    }
  }
`;

// ============================================================================
// Mutations
// ============================================================================

export const CREATE_ORDER_MUTATION = `
  mutation CreateOrder($input: CreateOrderInput!) {
    createOrder(input: $input) {
      id
      status
      totalAmount
      currency
    }
  }
`;

export const PROCEED_TO_PAYMENT_MUTATION = `
  mutation ProceedToPayment($orderId: ID!) {
    proceedToPayment(orderId: $orderId) {
      id
      status
    }
  }
`;

export const CANCEL_ORDER_MUTATION = `
  mutation CancelOrder($orderId: ID!) {
    cancelOrder(orderId: $orderId) {
      id
      status
    }
  }
`;

export const SELECT_PAYMENT_CURRENCY_MUTATION = `
  mutation SelectPaymentCurrency($orderId: ID!, $currency: String!) {
    selectPaymentCurrency(orderId: $orderId, currency: $currency) {
      id
      currency
      subtotal
      feesTotal
      totalAmount
      pricingOptions {
        id
        currency
        subtotal
        feesTotal
        total
        isSelected
        fees {
          id
          name
          type
          calculation
          amount
        }
      }
      selectedPricing {
        id
        currency
        subtotal
        feesTotal
        total
        fees {
          id
          name
          type
          calculation
          amount
        }
      }
    }
  }
`;

export const CONTACT_MANAGER_MUTATION = `
  mutation ContactManager($orderId: ID!, $message: String!) {
    contactManager(orderId: $orderId, message: $message)
  }
`;

// ============================================================================
// Admin Queries and Mutations
// ============================================================================

export const PENDING_PAYMENTS_QUERY = `
  query PendingPayments {
    pendingPayments {
      id
      status
      totalAmount
      currency
      createdAt
      event {
        id
        title
        startsAt
      }
      customer {
        id
        firstName
        lastName
        username
      }
      items {
        quantity
        ticketType {
          name
        }
      }
      receiptUrl
      receiptUploadedAt
    }
  }
`;

export const APPROVE_PAYMENT_MUTATION = `
  mutation ApprovePayment($orderId: ID!) {
    approvePayment(orderId: $orderId) {
      success
      message
    }
  }
`;

export const REJECT_PAYMENT_MUTATION = `
  mutation RejectPayment($orderId: ID!, $reason: String!) {
    rejectPayment(orderId: $orderId, reason: $reason) {
      success
      message
    }
  }
`;

export const ADMIN_ACCOUNTS_QUERY = `
  query AdminAccounts {
    adminAccounts {
      id
      firstName
      lastName
      username
      telegramId
    }
  }
`;

export const SEARCH_ACCOUNTS_QUERY = `
  query SearchAccounts($query: String!, $limit: Int) {
    searchAccounts(query: $query, limit: $limit) {
      id
      firstName
      lastName
      username
      telegramId
    }
  }
`;

export const ISSUE_TICKETS_FOR_CASH_MUTATION = `
  mutation IssueTicketsForCash($accountId: ID!, $eventId: ID!, $items: [CashTicketItemInput!]!) {
    issueTicketsForCash(accountId: $accountId, eventId: $eventId, items: $items) {
      success
      message
    }
  }
`;

// ============================================================================
// Admin Orders
// ============================================================================

export const ADMIN_ORDERS_QUERY = `
  query AdminOrders($filter: AdminOrdersFilter, $limit: Int, $offset: Int) {
    adminOrders(filter: $filter, limit: $limit, offset: $offset) {
      orders {
        id
        status
        totalAmount
        currency
        createdAt
        event {
          id
          title
          startsAt
        }
        customer {
          id
          firstName
          lastName
          username
        }
        items {
          quantity
          ticketType {
            name
          }
        }
        tickets {
          id
          ticketNumber
          status
          imageUrl
          issuedAt
        }
        receiptUrl
      }
      totalCount
    }
  }
`;

export const ADMIN_ORDER_QUERY = `
  query AdminOrder($id: ID!) {
    adminOrder(id: $id) {
      id
      status
      totalAmount
      currency
      createdAt
      event {
        id
        title
        startsAt
        venue
      }
      customer {
        id
        firstName
        lastName
        username
      }
      items {
        quantity
        unitPrice
        totalPrice
        ticketType {
          name
        }
      }
      tickets {
        id
        ticketNumber
        status
        imageUrl
        issuedAt
        ticketType {
          name
        }
      }
      receiptUrl
      receiptUploadedAt
    }
  }
`;

export const RESEND_TICKETS_MUTATION = `
  mutation ResendTickets($orderId: ID!, $options: ResendTicketsInput) {
    resendTickets(orderId: $orderId, options: $options) {
      success
      message
    }
  }
`;

/** Orders by same account (for "other orders" when sending message) */
export const ADMIN_ORDERS_BY_ACCOUNT_QUERY = `
  query AdminOrdersByAccount($accountId: ID!, $limit: Int) {
    adminOrdersByAccount(accountId: $accountId, limit: $limit) {
      orders {
        id
        status
        totalAmount
        currency
        createdAt
        event {
          id
          title
          startsAt
        }
        tickets {
          id
        }
      }
      totalCount
      statusCounts {
        status
        count
      }
    }
  }
`;

/** Conversation with user (messages to/from this account) */
export const ADMIN_USER_CONVERSATION_QUERY = `
  query AdminUserConversation($accountId: ID!, $limit: Int) {
    adminUserConversation(accountId: $accountId, limit: $limit) {
      id
      accountId
      sentByAccountId
      messageText
      orderId
      buttonText
      buttonTextEvent
      linkOrderId
      linkEventId
      createdAt
      sentBy {
        id
        firstName
        lastName
        username
      }
    }
  }
`;

/** Send custom message to user with button linking to order or event */
export const ADMIN_SEND_ORDER_MESSAGE_MUTATION = `
  mutation AdminSendOrderMessage($orderId: ID!, $message: String!, $buttonText: String, $buttonTextEvent: String, $linkOrderId: ID, $linkEventId: ID, $attachmentKind: MessageAttachmentKind) {
    adminSendOrderMessage(orderId: $orderId, message: $message, buttonText: $buttonText, buttonTextEvent: $buttonTextEvent, linkOrderId: $linkOrderId, linkEventId: $linkEventId, attachmentKind: $attachmentKind) {
      success
      message
    }
  }
`;

/** Upcoming events for link selector in send message modal */
export const ADMIN_UPCOMING_EVENTS_QUERY = `
  query AdminUpcomingEvents($limit: Int) {
    adminUpcomingEvents(limit: $limit) {
      id
      title
      startsAt
    }
  }
`;

export const GENERATE_TICKETS_MUTATION = `
  mutation GenerateTickets($orderId: ID!) {
    generateTickets(orderId: $orderId) {
      success
      message
    }
  }
`;

export const REGENERATE_TICKETS_MUTATION = `
  mutation RegenerateTickets($orderId: ID!, $options: RegenerateTicketsInput) {
    regenerateTickets(orderId: $orderId, options: $options) {
      success
      message
    }
  }
`;

/** Regenerate single ticket */
export const REGENERATE_TICKET_MUTATION = `
  mutation RegenerateTicket($ticketId: ID!, $options: RegenerateTicketsInput) {
    regenerateTicket(ticketId: $ticketId, options: $options) {
      success
      message
    }
  }
`;

/** Resend single ticket to user */
export const RESEND_TICKET_MUTATION = `
  mutation ResendTicket($ticketId: ID!, $options: ResendTicketsInput) {
    resendTicket(ticketId: $ticketId, options: $options) {
      success
      message
    }
  }
`;

/** Options for ticket regeneration */
export interface RegenerateTicketsOptions {
  /** If true, regenerate image only (keep existing ticket number). Default: true */
  imageOnly: boolean;
  /** If true, send tickets to user after regeneration. Default: true */
  sendToUser: boolean;
}

// ============================================================================
// Admin Events
// ============================================================================

export const ADMIN_EVENTS_QUERY = `
  query AdminEvents {
    adminEvents {
      id
      title
      description
      venue
      startsAt
      endsAt
      imageUrl
      videoUrl
      isActive
      createdAt
    }
  }
`;

export const ADMIN_EVENT_QUERY = `
  query AdminEvent($id: ID!) {
    adminEvent(id: $id) {
      id
      title
      description
      venue
      startsAt
      endsAt
      imageUrl
      videoUrl
      isActive
      featured
      createdAt
      ticketTypes {
        id
        name
        maxQuantity
        soldCount
        isActive
        prices {
          id
          currency
          price
        }
        price
        currency
        fees {
          feeConfiguration {
            id
            type
            prices {
              currency
              amount
            }
          }
        }
      }
    }
    feeConfigurations {
      id
      name
      type
      calculation
      prices {
        id
        currency
        amount
      }
    }
  }
`;

export const CREATE_EVENT_MUTATION = `
  mutation CreateEvent($input: EventInput!) {
    createEvent(input: $input) {
      id
      title
      isActive
    }
  }
`;

export const UPDATE_EVENT_MUTATION = `
  mutation UpdateEvent($id: ID!, $input: EventInput!) {
    updateEvent(id: $id, input: $input) {
      id
      title
      isActive
    }
  }
`;

export const DELETE_EVENT_MUTATION = `
  mutation DeleteEvent($id: ID!) {
    deleteEvent(id: $id)
  }
`;

export const DUPLICATE_EVENT_MUTATION = `
  mutation DuplicateEvent($id: ID!) {
    duplicateEvent(id: $id) {
      id
      title
    }
  }
`;

export const CREATE_TICKET_TYPE_MUTATION = `
  mutation CreateTicketType($eventId: ID!, $input: TicketTypeInput!) {
    createTicketType(eventId: $eventId, input: $input) {
      id
      name
      maxQuantity
      soldCount
      isActive
      prices {
        id
        currency
        price
      }
      price
      currency
    }
  }
`;

export const UPDATE_TICKET_TYPE_MUTATION = `
  mutation UpdateTicketType($id: ID!, $input: TicketTypeUpdateInput!) {
    updateTicketType(id: $id, input: $input) {
      id
      name
      maxQuantity
      soldCount
      isActive
      prices {
        id
        currency
        price
      }
      price
      currency
    }
  }
`;

export const DELETE_TICKET_TYPE_MUTATION = `
  mutation DeleteTicketType($id: ID!) {
    deleteTicketType(id: $id)
  }
`;

/** Event with ticket types for cash ticket issuance (multi-currency). */
export const EVENT_WITH_TICKET_TYPES_QUERY = `
  query EventWithTicketTypes($id: ID!) {
    event(id: $id) {
      id
      title
      ticketTypes {
        id
        name
        description
        price
        totalPrice
        currency
        maxQuantity
        soldCount
        available
        isActive
        prices {
          id
          currency
          price
        }
        fees {
          id
          isActive
          overrideAmount
          feeConfiguration {
            id
            type
            calculation
            amount
            currency
            prices {
              currency
              amount
            }
          }
        }
        feesBreakdown {
          name
          type
          amount
        }
      }
    }
  }
`;

// ============================================================================
// Types - Re-exported from generated GraphQL types
// ============================================================================

// Re-export generated types for use throughout the app
// Run `yarn generate` in webapp to regenerate after schema changes
// Note: Event is imported with alias to avoid conflict with DOM Event
import type { Event as GraphQLEvent } from "../generated/graphql.ts";
export type { GraphQLEvent as Event };
export type {
  // Domain types (Event re-exported above)
  TicketType,
  FeeBreakdown,
  Order,
  OrderItem,
  OrderFee,
  Ticket,
  ManualPayment,
  PaymentRequisites,
  PendingPayment,
  PaymentActionResult,
  Customer,
  // Enums
  FeeType,
  OrderStatus,
  TicketStatus,
} from "../generated/graphql.ts";

/** Event content fields (subtitle, streams, etc.). Present on Event after schema/codegen update. */
export type EventContentFields = {
  subtitle?: string | null;
  address?: string | null;
  mapsUrl?: string | null;
  videoUrl?: string | null;
  streamYoutubeUrl?: string | null;
  streamVkUrl?: string | null;
  telegramChatUrl?: string | null;
  inviteText?: string | null;
  slogan?: string | null;
  program?: string | null;
};

/** Event with content fields (for pages that use subtitle, program, streams). */
export type EventWithContent = GraphQLEvent & EventContentFields;

/**
 * Type guard to check if event has extended content fields.
 * Use instead of unsafe `as EventWithContent` type assertions.
 */
export function hasEventContentFields(
  event: GraphQLEvent | null | undefined
): event is EventWithContent {
  if (!event) return false;
  // Check if any content field is present (not just undefined)
  return (
    "program" in event ||
    "streamYoutubeUrl" in event ||
    "streamVkUrl" in event ||
    "telegramChatUrl" in event ||
    "inviteText" in event ||
    "slogan" in event ||
    "mapsUrl" in event
  );
}

/**
 * Type guard to check if event has a home group.
 */
export function hasHomeGroup(
  event: GraphQLEvent | null | undefined
): event is GraphQLEvent & { homeGroup: { id: string; name: string } } {
  if (!event) return false;
  return (
    "homeGroup" in event &&
    event.homeGroup !== null &&
    typeof event.homeGroup === "object" &&
    "id" in event.homeGroup
  );
}

/** Admin list item for bot users (cash ticket issuance). Not in generated schema yet. */
export type AdminAccount = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  telegramId: string;
};
